<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useToast } from 'primevue/usetoast';
import OptionConfig from '@/components/configuration/OptionConfig.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const isLoading = ref(false);
const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const adminSetupStore = useAdminSetupStore();
const listOptionPermitConfig: any = ref([]);
const listHotelOptionConfig: any = ref([]);
const listRoomPerHotelConfig: any = ref([]);
const payload: any = ref({
  pk: null,
  sk: null,
  packageOptionConfig: {
    maxOptionPermitted: 0,
    maxHotelPerOption: 0,
    maxRoomPerHotel: 0,
    incentiveMaxOptionPermitted: 0,
    incentiveMaxHotelPerOption: 0,
    incentiveMaxRoomPerHotel: 0
  },
  hotelOptionConfig: {
    maxOptionPermitted: 0,
    maxHotelPerOption: 0,
    maxRoomPerHotel: 0,
    incentiveMaxOptionPermitted: 0,
    incentiveMaxHotelPerOption: 0,
    incentiveMaxRoomPerHotel: 0
  },
  airOptionConfig: {
    maxOptionPermitted: 0,
    incentiveMaxOptionPermitted: 0
  },
  cruiseOptionConfig: {
    maxOptionPermitted: 0,
    maxHotelPerOption: 0,
    maxRoomPerHotel: 0,
    cruiseMaxCategory: 0,
    incentiveMaxOptionPermitted: 0,
    incentiveMaxHotelPerOption: 0,
    incentiveMaxRoomPerHotel: 0,
    incentiveCruiseMaxCategory: 0
  },
  mdtOptionConfig: {
    maxOptionPermitted: 0,
    incentiveMaxOptionPermitted: 0
  }
});

const formatConfigData = async (listData: any) => {
  const currentData = listData[0] ? listData[0] : listData;
  listOptionPermitConfig.value = [];
  listHotelOptionConfig.value = [];
  listRoomPerHotelConfig.value = [];
  currentData.cruiseTempOptionConfig = {
    ...currentData.cruiseOptionConfig
  };
  payload.value = listData;
  for (const key in currentData) {
    let dataObject: any = {};
    if (key.endsWith('OptionConfig')) {
      dataObject = { ...currentData[key], keyName: key };
      if (!dataObject.keyName.startsWith('cruiseTemp')) {
        listOptionPermitConfig.value.push(dataObject);
      }
      if (!dataObject.keyName.startsWith('air') && !dataObject.keyName.startsWith('mdt')) {
        listHotelOptionConfig.value.push(dataObject);
      }
      if (!dataObject.keyName.startsWith('air') && !dataObject.keyName.startsWith('mdt') && !dataObject.keyName.startsWith('cruiseTemp')) {
        listRoomPerHotelConfig.value.push(dataObject);
      }
    }
  }
};

const handleUpdateConfigOption = (data: any) => {
  payload.value = { ...payload.value, [data.keyName]: data };
  if (data.keyName === 'cruiseTempOptionConfig') {
    payload.value.cruiseOptionConfig = payload.value.cruiseTempOptionConfig;
  }
  delete payload?.value.cruiseTempOptionConfig;
  for (const key in payload.value) {
    if (key !== 'pk' && key !== 'sk') {
      for (const keyOfPayload in payload.value[key]) {
        if (payload.value[key][keyOfPayload] === null || payload.value[key][keyOfPayload] < 1) {
          payload.value[key][keyOfPayload] = 1;
        }
      }
    }
  }
  adminSetupStore
    .updateConfigOption(payload.value)
    .then(() => {
      adminSetupStore.getConfigOption().then((result) => {
        formatConfigData(result);
      });
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const getConfigOption = () => {
  isLoading.value = true;
  adminSetupStore
    .getConfigOption()
    .then((result) => {
      formatConfigData(result);
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getConfigOption();
</script>
<template>
  <div class="option-config">
    <LoadingSpinner v-if="isLoading" />
    <div v-else class="card">
      <h4>{{ $t('quote_config.option_configuration') }}</h4>
      <div class="grid mt-5">
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.max_option_permitted') }}</h5>
            <div v-for="(item, optionIndex) in listOptionPermitConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'maxOptionPermitted'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
          <div class="seperator mb-3 mt-5"></div>
        </div>
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.hotel_per_option') }}</h5>
            <div v-for="(item, optionIndex) in listHotelOptionConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'maxHotelPerOption'" :cruiseKey="'cruiseMaxCategory'" :typeOption="'hotel'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
          <div class="seperator mb-3 mt-5"></div>
        </div>
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.room_per_hotel') }}</h5>
            <div v-for="(item, optionIndex) in listRoomPerHotelConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'maxRoomPerHotel'" :typeOption="'room'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
        </div>
        <!-- incentive -->
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.incentive_max_option_permitted') }}</h5>
            <div v-for="(item, optionIndex) in listOptionPermitConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'incentiveMaxOptionPermitted'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
          <div class="seperator mb-3 mt-5"></div>
        </div>
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.incentive_hotel_per_option') }}</h5>
            <div v-for="(item, optionIndex) in listHotelOptionConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'incentiveMaxHotelPerOption'" :cruiseKey="'incentiveCruiseMaxCategory'" :typeOption="'hotel'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
          <div class="seperator mb-3 mt-5"></div>
        </div>
        <div class="col-option flex justify-content-between mb-3">
          <div>
            <h5 class="title">{{ t('quote_config.incentive_room_per_hotel') }}</h5>
            <div v-for="(item, optionIndex) in listRoomPerHotelConfig" :key="optionIndex">
              <OptionConfig :configData="item" :keys="'incentiveMaxRoomPerHotel'" :typeOption="'room'" @handleSubmit="(payload) => handleUpdateConfigOption(payload)"></OptionConfig>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
